/**
 * Roles.
 *
 * A role is a bundle of permission codes, and there are several hundred of
 * them. The editor groups the catalogue by the domain each code names, because
 * "everything in purchasing" is how an administrator thinks about it and a
 * flat list of three hundred checkboxes is not.
 *
 * Grants are replaced as a whole set, which is what the operation takes: there
 * is no moment where a role holds half of a change.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, ShieldCheck } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { localizedName } from "@/lib/i18n";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  createRole,
  getRole,
  replaceRolePermissions,
  updateRole,
  type Role,
  type RoleCreate,
} from "../api";
import { identityKeys, usePermissions, useRoles } from "../queries";

interface RoleDraft {
  code: string;
  name: string;
  nameAr: string;
  description: string;
  permissionCodes: string[];
}

const EMPTY_DRAFT: RoleDraft = {
  code: "",
  name: "",
  nameAr: "",
  description: "",
  permissionCodes: [],
};

export interface RolesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
}

export function RolesPage({ canCreate = true, canUpdate = true }: RolesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const rolesQuery = useRoles();
  const permissionsQuery = usePermissions();
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ role: Role; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<RoleDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<keyof RoleDraft, string>>>({});

  // Codes are "domain.resource.action"; the first segment is the domain an
  // administrator actually reasons about.
  const grouped = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const permission of permissionsQuery.data ?? []) {
      const domain = permission.code.split(".")[0] ?? "other";
      groups.set(domain, [...(groups.get(domain) ?? []), permission.code]);
    }
    return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right));
  }, [permissionsQuery.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: identityKeys.roles.all });
  };

  const openCreate = (): void => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setErrors({});
    setDialogOpen(true);
  };

  const loadForEdit = useMutation({
    mutationFn: (roleId: string) => getRole(roleId),
    onSuccess: ({ data, etag }) => {
      setEditing({ role: data, etag });
      setDraft({
        code: data.code,
        name: data.name,
        nameAr: data.name_ar ?? "",
        description: data.description ?? "",
        permissionCodes: [...data.permission_codes],
      });
      setErrors({});
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const onWriteError = (error: unknown): void => {
    if (error instanceof ApiError && (error.status === 409 || error.status === 422)) {
      setErrors({ code: error.problem?.detail ?? t("errors.generic") });
      return;
    }
    surfaceProblem(error, t);
  };

  const createMutation = useIdempotentMutation<Role, RoleCreate>({
    mutationFn: (input, key) => createRole(input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: onWriteError,
  });

  const saveMutation = useMutation({
    mutationFn: async (input: { role: Role; etag: string; draft: RoleDraft }) => {
      let etag = input.etag;
      const arabic = input.draft.nameAr.trim();
      const description = input.draft.description.trim();
      const patch: Record<string, unknown> = {};
      if (input.draft.name.trim() !== input.role.name) patch.name = input.draft.name.trim();
      if (arabic !== (input.role.name_ar ?? "")) patch.name_ar = arabic === "" ? null : arabic;
      if (description !== (input.role.description ?? "")) patch.description = description;
      if (Object.keys(patch).length > 0) {
        const result = await updateRole(input.role.id, patch, etag);
        etag = result.etag ?? etag;
      }
      if (!sameSet(input.draft.permissionCodes, input.role.permission_codes)) {
        await replaceRolePermissions(input.role.id, input.draft.permissionCodes, etag);
      }
    },
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: onWriteError,
  });

  const validate = (): boolean => {
    const next: Partial<Record<keyof RoleDraft, string>> = {};
    if (editing === null && !/^[a-z][a-z0-9_-]*$/.test(draft.code.trim())) {
      next.code = t("identity.roles.codeFormat");
    }
    if (draft.name.trim() === "") next.name = t("identity.roles.nameRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (): void => {
    if (!validate()) return;
    if (editing === null) {
      const arabic = draft.nameAr.trim();
      const description = draft.description.trim();
      createMutation.mutate({
        code: draft.code.trim(),
        name: draft.name.trim(),
        ...(arabic === "" ? {} : { name_ar: arabic }),
        ...(description === "" ? {} : { description }),
        permission_codes: draft.permissionCodes,
      });
      return;
    }
    if (editing.etag === null) {
      surfaceProblem(new Error(t("errors.generic")), t);
      return;
    }
    saveMutation.mutate({ role: editing.role, etag: editing.etag, draft });
  };

  const toggleDomain = (codes: string[], checked: boolean): void => {
    setDraft((current) => ({
      ...current,
      permissionCodes: checked
        ? [...new Set([...current.permissionCodes, ...codes])]
        : current.permissionCodes.filter((code) => !codes.includes(code)),
    }));
  };

  const saving = createMutation.isPending || saveMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">
          {t("identity.roles.title")}
        </h1>
        {canCreate ? (
          <Button onClick={openCreate}>
            <Plus className="me-2 size-4" aria-hidden="true" />
            {t("identity.roles.new")}
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("identity.roles.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {rolesQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("errors.generic")}
            </p>
          ) : rolesQuery.isPending ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("table.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{t("identity.roles.code")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.roles.name")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.roles.grants")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.roles.holders")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.roles.status")}</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr
                      key={role.id}
                      className="border-b transition-colors last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs">{role.code}</td>
                      <td className="px-4 py-3 align-top">
                        {localizedName(role.name, role.name_ar, i18n.language)}
                      </td>
                      <td className="px-4 py-3 align-top tabular-nums">
                        {role.permission_codes.length}
                      </td>
                      <td className="px-4 py-3 align-top tabular-nums text-muted-foreground">
                        {role.assigned_user_count ?? 0}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge variant={role.status === "active" ? "default" : "secondary"}>
                          {t(`status.${role.status}`, role.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-top text-end">
                        {canUpdate ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadForEdit.mutate(role.id)}
                            aria-label={`${t("identity.roles.edit")} ${role.code}`}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === null ? t("identity.roles.new") : t("identity.roles.edit")}
            </DialogTitle>
            <DialogDescription>{t("identity.roles.formHint")}</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("identity.roles.code")} error={errors.code} htmlFor="role-code">
                <Input
                  id="role-code"
                  dir="ltr"
                  disabled={editing !== null}
                  value={draft.code}
                  onChange={(event) => setDraft({ ...draft, code: event.target.value })}
                />
              </Field>
              <Field label={t("identity.roles.nameEn")} error={errors.name} htmlFor="role-name">
                <Input
                  id="role-name"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
            </div>

            <Field label={t("identity.roles.nameAr")} htmlFor="role-name-ar">
              <Input
                id="role-name-ar"
                dir="rtl"
                lang="ar"
                value={draft.nameAr}
                onChange={(event) => setDraft({ ...draft, nameAr: event.target.value })}
              />
            </Field>

            <Field label={t("identity.roles.description")} htmlFor="role-description">
              <Input
                id="role-description"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>

            <div className="flex flex-col gap-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="size-4" aria-hidden="true" />
                {t("identity.roles.grants")}
                <span className="text-xs font-normal text-muted-foreground">
                  {t("identity.roles.grantCount", { count: draft.permissionCodes.length })}
                </span>
              </p>
              {permissionsQuery.isPending ? (
                <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {grouped.map(([domain, codes]) => {
                    const selected = codes.filter((code) => draft.permissionCodes.includes(code));
                    return (
                      <details key={domain} className="rounded-lg border">
                        <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                          <span className="font-medium">{domain}</span>
                          <span className="text-xs text-muted-foreground">
                            {selected.length}/{codes.length}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="ms-auto"
                            onClick={(event) => {
                              event.preventDefault();
                              toggleDomain(codes, selected.length !== codes.length);
                            }}
                          >
                            {selected.length === codes.length
                              ? t("identity.roles.clearDomain")
                              : t("identity.roles.selectDomain")}
                          </Button>
                        </summary>
                        <div className="grid gap-1 border-t px-3 py-2 sm:grid-cols-2">
                          {codes.map((code) => (
                            <label key={code} className="flex items-center gap-2 text-xs">
                              <Checkbox
                                checked={draft.permissionCodes.includes(code)}
                                onCheckedChange={(value) =>
                                  setDraft((current) => ({
                                    ...current,
                                    permissionCodes:
                                      value === true
                                        ? [...current.permissionCodes, code]
                                        : current.permissionCodes.filter((item) => item !== code),
                                  }))
                                }
                              />
                              <span dir="ltr" className="font-mono">
                                {code}
                              </span>
                            </label>
                          ))}
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={saving}>
                {t("actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const seen = new Set(right);
  return left.every((value) => seen.has(value));
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error !== undefined ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
